import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, ChevronDown, Download, ChevronLeft, ChevronRight, Play, Calendar } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';

// (audit 2026-06-04) — page previously rendered a hardcoded mock list of
// #VR-9821 / Alex Thompson / Modern Living Apts etc. Now fetches the real
// queue from /api/admin/qa-jobs and derives tab counts + recent activity
// from real data. The "Open QA Review" row button (existing) now opens the
// real job's QA detail page.
const PAGE_SIZE = 10;

const QualityAssurancePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('awaiting');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [qaJobs, setQaJobs] = useState<any[]>([]);

  // (audit 2026-06-11) — wired the Service Type / Date Range filters and
  // pagination. Filters are in-memory because /admin/qa-jobs returns the
  // current queue; for very large queues we'd push to the backend instead.
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('all'); // all | today | 7d | 30d

  const fmtGBP = (pence?: number | null) =>
    '£' + ((Number(pence) || 0) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (iso?: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const statusColorFor = (s?: string) =>
    s === 'Passed QA' ? 'green'
      : s === 'Failed/Returned' ? 'red'
      : s === 'Under Review' ? 'blue'
      : 'orange';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.request('/admin/qa-jobs', 'GET');
        if (cancelled) return;
        const list = res?.success ? (res.data?.qaJobs ?? res.data ?? []) : [];
        setQaJobs((list as any[]).map((j) => ({
          id: j.qa_id || ('#QA-' + j.job_id),
          jobId: j.job_id,
          serviceType: j.service || 'Service',
          provider: j.provider || 'Unassigned',
          customer: j.customer || 'Unknown',
          property: j.property || '—',
          completed: fmtDate(j.job_completed_at),
          completedRaw: j.job_completed_at,           // kept for filtering
          payout: fmtGBP(j.amount_pence),
          status: j.status || 'Pending Review',
          statusColor: statusColorFor(j.status),
        })));
      } catch (err) {
        console.error('QA jobs fetch failed:', err);
        if (!cancelled) setQaJobs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Derived counts so the tab badges reflect real data instead of '14'/'3' literals.
  const countByStatus = (s: string) => qaJobs.filter((j) => j.status === s).length;
  const tabs = [
    { id: 'awaiting', label: 'Awaiting Review', count: countByStatus('Pending Review') },
    { id: 'review',   label: 'Under Review',    count: countByStatus('Under Review')    },
    { id: 'passed',   label: 'Passed',          count: countByStatus('Passed QA')        },
    { id: 'returned', label: 'Returned',        count: countByStatus('Failed/Returned')  },
    { id: 'closed',   label: 'Closed' },
  ];

  // Top-card metrics derived from real data (was hardcoded "14/+2", "8/84%"
  // etc.). "Today" buckets use the job_completed_at timestamp.
  const isToday = (iso?: string | null) => {
    if (!iso) return false;
    const d = new Date(iso); if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };
  const isYesterday = (iso?: string | null) => {
    if (!iso) return false;
    const d = new Date(iso); if (Number.isNaN(d.getTime())) return false;
    const y = new Date(); y.setDate(y.getDate() - 1);
    return d.getFullYear() === y.getFullYear() && d.getMonth() === y.getMonth() && d.getDate() === y.getDate();
  };

  const awaitingCount = countByStatus('Pending Review');
  const awaitingYesterday = qaJobs.filter((j) => j.status === 'Pending Review' && isYesterday(j.completedRaw)).length;
  const awaitingDelta = awaitingCount - awaitingYesterday;
  const passedToday = qaJobs.filter((j) => j.status === 'Passed QA' && isToday(j.completedRaw)).length;
  const failedToday = qaJobs.filter((j) => j.status === 'Failed/Returned' && isToday(j.completedRaw)).length;
  const decidedToday = passedToday + failedToday;
  const successRate = decidedToday > 0 ? Math.round((passedToday / decidedToday) * 100) : 0;

  // Service Type list comes from the loaded data — admin can filter to any
  // type that actually exists in the current queue.
  const serviceTypeOptions = Array.from(new Set(qaJobs.map((j) => j.serviceType))).filter((s) => s && s !== 'Service');

  // In-memory filtering pipeline.
  const filterByDate = (j: any) => {
    if (dateRangeFilter === 'all') return true;
    if (!j.completedRaw) return false;
    const d = new Date(j.completedRaw); if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    if (dateRangeFilter === 'today') return isToday(j.completedRaw);
    if (dateRangeFilter === '7d') return (now.getTime() - d.getTime()) <= 7 * 24 * 60 * 60 * 1000;
    if (dateRangeFilter === '30d') return (now.getTime() - d.getTime()) <= 30 * 24 * 60 * 60 * 1000;
    return true;
  };
  const filteredJobs = qaJobs
    .filter((j) => serviceTypeFilter === 'all' || j.serviceType === serviceTypeFilter)
    .filter(filterByDate)
    .filter((j) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return (
        String(j.id).toLowerCase().includes(q) ||
        String(j.jobId).toLowerCase().includes(q) ||
        String(j.provider).toLowerCase().includes(q) ||
        String(j.customer).toLowerCase().includes(q)
      );
    });

  // Reset to page 1 when filters change.
  useEffect(() => { setCurrentPage(1); }, [serviceTypeFilter, dateRangeFilter, searchQuery, activeTab]);

  // Pagination calculations.
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filteredJobs.length);
  const pagedJobs = filteredJobs.slice(pageStart, pageEnd);

  // Recent activity is now derived from real jobs (most recent first).
  const recentActivity = qaJobs.slice(0, 3).map((j, i) => ({
    id: i + 1,
    text: `Job ${j.id} — ${j.status}`,
    detail: `${j.provider} • ${j.completed}`,
    color: j.statusColor === 'green' ? 'teal' : j.statusColor === 'red' ? 'red' : 'teal',
  }));

  const handleDownloadChecklist = () => {
    // Generate a JSON download of the current QA queue snapshot so admins
    // have an auditable record. Was previously an inert "Download QA
    // Checklist" button with no onClick.
    const snapshot = {
      generatedAt: new Date().toISOString(),
      metrics: {
        awaitingReview: awaitingCount,
        passedToday,
        failedToday,
        successRate: successRate + '%',
      },
      filters: { activeTab, serviceTypeFilter, dateRangeFilter, searchQuery },
      jobs: filteredJobs,
    };
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-snapshot-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Quality Assurance - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 p-8 min-h-screen">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Quality Assurance</h1>
            <p className="text-gray-600">Review completed jobs before approving closure and releasing provider payouts.</p>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-xs uppercase font-semibold text-gray-600 mb-2">Awaiting QA Review</p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-gray-900">{awaitingCount}</p>
                <p className="text-sm text-orange-600 font-semibold">
                  {awaitingDelta >= 0 ? '+' : ''}{awaitingDelta} from yesterday
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <p className="text-xs uppercase font-semibold text-gray-600 mb-2">Passed QA Today</p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-gray-900">{passedToday}</p>
                <p className="text-sm text-green-600 font-semibold">
                  {decidedToday > 0 ? `${successRate}% Success` : '—'}
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-xs uppercase font-semibold text-gray-600 mb-2">Failed/Returned Today</p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-gray-900">{failedToday}</p>
                <p className="text-sm text-red-600 font-semibold">
                  {decidedToday > 0 ? `${100 - successRate}% return rate` : '—'}
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-xs uppercase font-semibold text-gray-600 mb-2">Total in Queue</p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-gray-900">{qaJobs.length}</p>
                <p className="text-sm text-blue-600 font-semibold">{filteredJobs.length} shown</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Top Section - Jobs Table Full Width */}
            <div>
              {/* Jobs Table */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-teal-600 text-teal-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                    {tab.count && <span className="ml-2 text-xs text-gray-500">({tab.count})</span>}
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              <div className="p-6 border-b border-gray-200 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Job ID, Provider..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select
                    value={serviceTypeFilter}
                    onChange={(e) => setServiceTypeFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="all">All Service Types</option>
                    {serviceTypeOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <select
                    value={dateRangeFilter}
                    onChange={(e) => setDateRangeFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 flex items-center gap-2"
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-6 py-4">
                        <input type="checkbox" className="w-4 h-4 rounded" />
                      </th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Job ID</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Service Type</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Provider / Customer</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Property</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Completed</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Payout</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="text-left px-4 py-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedJobs.map((job) => (
                      <tr key={job.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <input type="checkbox" className="w-4 h-4 rounded" />
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-teal-600">{job.id}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-900">{job.serviceType}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-medium text-gray-900">{job.provider}</p>
                          <p className="text-xs text-gray-600">{job.customer}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-900">{job.property}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-900">{job.completed}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-semibold text-gray-900">{job.payout}</p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="inline-block">
                            <span className={`px-3 py-1 rounded text-xs font-semibold ${
                              job.statusColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                              job.statusColor === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                          {job.reviewedBy && (
                            <p className="text-xs text-gray-600 mt-1">Reviewing by {job.reviewedBy}</p>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <Button 
                            onClick={() => navigate(`/admin/qa/${job.jobId}`)}
                            className="bg-teal-600 hover:bg-teal-700 text-white text-sm h-9"
                          >
                            Open QA Review
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {filteredJobs.length === 0
                    ? 'No matching jobs'
                    : `Showing ${pageStart + 1} to ${pageEnd} of ${filteredJobs.length} entries`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                    <Button
                      key={n}
                      size="sm"
                      onClick={() => setCurrentPage(n)}
                      className={n === currentPage ? 'bg-teal-600 text-white' : ''}
                      variant={n === currentPage ? 'default' : 'outline'}
                    >
                      {n}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            </div>

            {/* Bottom Section - 2 Column Layout */}
            <div className="grid grid-cols-2 gap-8">
              {/* QA Guidelines Card */}
              <div className="bg-gradient-to-br from-teal-700 to-teal-800 text-white rounded-lg p-8 shadow-lg">
                <h3 className="text-xl font-bold mb-3">QA Guidelines & Best Practices</h3>
                <p className="text-sm text-teal-100 mb-6">
                  Ensure all ratings evidence clearly shows the installation is complete with serial numbers and final signal testing results before authorizing payout releases.
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleDownloadChecklist}
                    className="bg-white text-teal-700 hover:bg-gray-100 font-semibold flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download QA Checklist
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/help-faqs?category=qa-training')}
                    className="border-white text-white hover:bg-teal-600 flex items-center justify-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    View Training Video
                  </Button>
                </div>
              </div>

              {/* Recent QA Activity Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">RECENT QA ACTIVITY</h3>
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="pb-4 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 mt-2 rounded-full ${
                          activity.color === 'teal' ? 'bg-teal-500' : 'bg-red-500'
                        }`}></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.text}</p>
                          <p className="text-xs text-gray-600">{activity.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/configuration-status?view=audit-log&entity=qa')}
                  className="w-full mt-4 text-teal-600 text-sm font-medium"
                >
                  View Full Audit Log
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default QualityAssurancePage;
